import React, { useRef, useState } from 'react'
import { View, Text, Icon, PinnedBannerModal } from '@/components'
import { createStyles } from '@codeleap/styles'
import { Channel } from 'stream-chat'
import { useIsMobile, usePinnedMessage } from '@/hooks'
import { chatPreviewText, attachmentIcon, attachmentPreviewText, pinnedMessagePreviewText } from '@packages/chat'
import { PinnedMessageMenu } from './PinnedMessageMenu'

export type PinnedMessageBannerProps = {
  channel: Channel
}

export const PinnedMessageBanner = (props: PinnedMessageBannerProps) => {
  const { channel } = props
  const bannerRef = useRef<HTMLDivElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isMobile = useIsMobile()

  const { pinnedMessage } = usePinnedMessage(channel.id, channel.type)

  const visible = !!pinnedMessage?.id

  if (!visible) return null

  const attachment = pinnedMessage?.attachments?.[0]
  const hasText = !!pinnedMessage?.text?.trim()

  const previewText = pinnedMessagePreviewText(pinnedMessage)

  const iconName = attachmentIcon(attachment)

  const handleClick = () => {
    if (isMobile) {
      setIsModalOpen(true)
    } else {
      setIsMenuOpen(true)
    }
  }

  return (
    <>
      <View
        ref={bannerRef}
        style={[styles.wrapper]}
        className='pinned-message-banner'
        onClick={handleClick}
      >
        <View style={[styles.content]}>
          <View style={[styles.iconWrapper]}>
            <Icon name='pin-01' size={24} style={[styles.icon]} />
          </View>
          <View style={[styles.textContainer]}>
            <View style={[styles.previewContainer]}>
              {attachment && iconName && (
                <Icon name={iconName as any} size={16} style={[styles.attachmentIcon]} />
              )}
              <Text
                text={previewText || 'No preview available'}
                style={[styles.preview]}
              />
            </View>
          </View>
        </View>
      </View>

      {isMobile ? (
        <PinnedBannerModal
          visible={isModalOpen}
          toggle={() => setIsModalOpen(false)}
          channel={channel}
          message={pinnedMessage}
        />
      ) : (
        <PinnedMessageMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          channel={channel}
          message={pinnedMessage}
          anchorRef={bannerRef}
        />
      )}
    </>
  )
}

export const PINNED_BANNER_HEIGHT = 64

const styles = createStyles(theme => ({
  wrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    gap: theme.spacing.value(1.5),
    backgroundColor: theme.colors.bgDefault,
    boxShadow: theme.effects.light.boxShadow,
    borderBottom: `1px solid ${theme.baseColors.neutralBlackTransparent100}`,
    height: PINNED_BANNER_HEIGHT,
    ...theme.spacing.paddingHorizontal(3),
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.value(1.5),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  icon: {
    fontSize: 18,
    color: theme.colors.textCaption,
    flexShrink: 0,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.value(0.5),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.value(0.5),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  attachmentIcon: {
    fontSize: 16,
    color: theme.colors.textCaption,
    flexShrink: 0,
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.baseColors.primarySolid500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  preview: {
    fontSize: 14,
    color: theme.colors.textHeadingRegular,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconWrapper: {
    ...theme.presets.center,
    width: 40,
    height: 40,
    backgroundColor: theme.colors.buttonNeutralSecondaryBgDisabled,
    borderRadius: theme.radius.small,
    padding: 0,
  },
}))
